<template>
  <!-- ===================== HEADER SEARCH (compactMode) ===================== -->
  <form v-if="compactMode" class="header-search-wrap" @submit.prevent="onSubmitSearch">
    <label class="header-search-bar">
      <span class="header-search-icon">
        <i class="uil-search"></i>
      </span>
      <input ref="searchInput"
             v-model="search"
             :placeholder="$t('general.dashboard_search')"
             autocomplete="off"
             type="text"
             @blur="inputBlur"
             @focus="modal = true"
      />
    </label>

    <!-- Dropdown -->
    <div v-if="modal" class="header-search-dropdown">
      <default-loading v-if="searching" :minHeight="'200px'"></default-loading>
      <template v-else>
        <div class="header-search-dropdown-inner">
          <!-- Assignments -->
          <div v-if="assignmentVisible && allItems.assignments.length" class="hs-group hs-group--assign">
            <h4 class="hs-group-title">
              <i class="uil-book-open"></i>
              {{ $t('sideNav.my_assigned_education') }}
              <span class="hs-count">{{ allItems.assignments_total }}</span>
            </h4>
            <button v-for="(assignment, index) in allItems.assignments"
                    :key="'hs_assign_' + index"
                    :class="{ 'hs-item--active': isActiveItem('user.assignment-detail', assignment.id) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openAssignmentDetails(assignment.id)">
              <span class="hs-item-label">{{ assignment.name }}</span>
            </button>
          </div>

          <!-- Collections -->
          <div v-if="collectionVisible && allItems.collections.length" class="hs-group hs-group--collection">
            <h4 class="hs-group-title">
              <i class="uil-notebooks"></i>
              {{ collectionTitle }}
              <span class="hs-count">{{ allItems.collections_total }}</span>
            </h4>
            <button v-for="(collection, index) in allItems.collections"
                    :key="'hs_coll_' + index"
                    :class="{ 'hs-item--active': isActiveItem('user.collection-detail', collection.id) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openCollectionDetails(collection.id)">
              <span class="hs-item-label">{{ collection.name }}</span>
            </button>
          </div>

          <!-- Social Posts -->
          <div v-if="wallVisible && allItems.posts.length" class="hs-group hs-group--post">
            <h4 class="hs-group-title">
              <i class="uil-coffee"></i>
              {{ $t("home.Wall") }}
              <span class="hs-count">{{ allItems.posts_total }}</span>
            </h4>
            <button v-for="(post, index) in allItems.posts"
                    :key="'hs_post_' + index"
                    :class="{ 'hs-item--active': isActiveItem('user.wall', post.id) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openSocialPost(post.id)">
              <span class="hs-item-label">{{ post.text }}</span>
            </button>
          </div>

          <!-- LinkedIn -->
          <div v-if="linkedinVisible && allItems.linkedinResults.length" class="hs-group hs-group--linkedin">
            <h4 class="hs-group-title">
              {{ $t('home.provider_trainings') }}
              <span class="hs-count">{{ allItems.linkedinResults.length }}</span>
            </h4>
            <button v-for="(result, index) in allItems.linkedinResults"
                    :key="'hs_linkedin_' + index"
                    :class="{ 'hs-item--active': isActiveProviderItem(result) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openProviderDetails(result)">
              <span class="hs-item-label">{{ result.text }}</span>
            </button>
          </div>

          <!-- Udemy -->
          <div v-if="udemyVisible && allItems.udemyResults.length" class="hs-group hs-group--udemy">
            <h4 class="hs-group-title">
              {{ $t('home.provider_udemy') }}
              <span class="hs-count">{{ allItems.udemyResults.length }}</span>
            </h4>
            <button v-for="(result, index) in allItems.udemyResults"
                    :key="'hs_udemy_' + index"
                    :class="{ 'hs-item--active': isActiveProviderItem(result) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openProviderDetails(result)">
              <span class="hs-item-label">{{ result.text }}</span>
            </button>
          </div>

          <!-- Harvard -->
          <div v-if="harvardVisible && allItems.harvardResults.length" class="hs-group hs-group--harvard">
            <h4 class="hs-group-title">
              {{ $t('home.provider_harvard') }}
              <span class="hs-count">{{ allItems.harvardResults.length }}</span>
            </h4>
            <button v-for="(result, index) in allItems.harvardResults"
                    :key="'hs_harvard_' + index"
                    :class="{ 'hs-item--active': isActiveProviderItem(result) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openProviderDetails(result)">
              <span class="hs-item-label">{{ result.text }}</span>
            </button>
          </div>

          <!-- Aeroclass -->
          <div v-if="aeroclassVisible && allItems.aeroclassResults.length" class="hs-group hs-group--aeroclass">
            <h4 class="hs-group-title">
              {{ $t('home.provider_aeroclass') }}
              <span class="hs-count">{{ allItems.aeroclassResults.length }}</span>
            </h4>
            <button v-for="(result, index) in allItems.aeroclassResults"
                    :key="'hs_aeroclass_' + index"
                    :class="{ 'hs-item--active': isActiveProviderItem(result) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openProviderDetails(result)">
              <span class="hs-item-label">{{ result.text }}</span>
            </button>
          </div>

          <!-- Coursera -->
          <div v-if="courseraVisible && allItems.courseraResults.length" class="hs-group hs-group--coursera">
            <h4 class="hs-group-title">
              {{ $t('home.provider_coursera') }}
              <span class="hs-count">{{ allItems.courseraResults.length }}</span>
            </h4>
            <button v-for="(result, index) in allItems.courseraResults"
                    :key="'hs_coursera_' + index" :class="{ 'hs-item--active': isActiveProviderItem(result) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openProviderDetails(result)">
              <span class="hs-item-label">{{ result.text }}</span>
            </button>
          </div>

          <!-- Catalog -->
          <div v-if="catalogVisible && allItems.catalogResults.length" class="hs-group hs-group--catalog">
            <h4 class="hs-group-title">
              {{ $t("education.catalog_education") }}
              <span class="hs-count">{{ allItems.catalogResultsTotal }}</span>
            </h4>
            <button v-for="(result, index) in allItems.catalogResults"
                    :key="'hs_catalog_' + index"
                    :class="{ 'hs-item--active': isActiveItem('user.education-detail', result.id) }"
                    class="hs-item"
                    type="button"
                    @mousedown.prevent="openCatalogDetails(result.id)">
              <span class="hs-item-label">{{ result.text }}</span>
            </button>
          </div>
        </div>
      </template>
    </div>
  </form>

  <!-- ===================== DASHBOARD SEARCH (normal) ===================== -->
  <form v-else action="" @submit.prevent="onSubmitSearch">
    <!-- Skeleton Loading -->
    <template v-if="loading && shouldShowSearchbar">
      <div class="section-header-left mb-4 searchbar searchbar-skeleton position-relative" :style="searchbarStyle">
        <div class="searchbar-top-right-slot">
          <slot name="top-right"></slot>
        </div>
        <h2 class="skeleton-title"></h2>
        <div class="position-relative">
          <div class="input-items-group d-flex justify-content-between align-items-center">
            <div class="skeleton-input mt-2"></div>
            <button class="btn skeleton-button" type="button">
              <i class="uil-search"></i>
            </button>
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="!company">
      <default-loading :minHeight="'340px'"></default-loading>
    </template>
    <template v-else>
      <!-- Top Right Slot - search alanı gizliyse göster -->
      <div v-if="!shouldShowSearchbar" class="top-right-slot-only">
        <slot name="top-right"></slot>
      </div>

      <div v-if="shouldShowSearchbar" :style="searchbarStyle"
           class="searchbar section-header-left mb-2 position-relative">
        <div class="searchbar-top-right-slot">
          <slot name="top-right"></slot>
        </div>
        <h2>
          {{
            $t("home.What_Would_You_Like_To_Learn_Today_Username").replace(
                ":NAME",
                current_user.name
            )
          }}
        </h2>
        <div class="position-relative">
          <div class="
            input-items-group
            d-flex
            justify-content-between
            align-items-center
          ">
            <input ref="searchInput" v-model="search" :placeholder="$t('filters.search')" autocomplete="off"
                   class="mt-2"
                   type="text" @blur="inputBlur" @focus="modal = true"/>
            <button class="btn" type="submit">
              <i class="uil-search"></i>
            </button>
          </div>
          <div v-if="modal" class="list-group mt-1 position-absolute w-100 z-100 shadow-lg searchbar-result">
            <div class="list-group-item list-group-item-action p-3 searchbar-result-inner">

              <!-- LOADING BOX -->
              <default-loading v-if="searching" :minHeight="'340px'"></default-loading>
              <template v-else>
                <!-- SEARCH ITEMS -->
                <div v-if="assignmentVisible && allItems.assignments.length" class="search-result-group">
                  <h4 class="mt-2 mb-2">
                    <i class="uil-book-open"></i> {{ $t('sideNav.my_assigned_education') }}
                    <span class="search-result-count">{{ allItems.assignments_total }}</span>
                  </h4>
                  <a v-for="(assignment, index) in allItems.assignments" :key="'search_post_list_item_' + index"
                    class="list-group-item list-group-item-action" href="javascript:void(0)"
                    @click="openAssignmentDetails(assignment.id)">
                    {{ assignment.name }}
                  </a>
                </div>
                <div v-if="collectionVisible && allItems.collections.length" class="search-result-group">
                  <h4 class="mt-4 mb-2"><i class="uil-notebooks"></i> {{ collectionTitle }}
                    <span class="search-result-count">{{ allItems.collections_total }}</span>
                  </h4>
                  <a v-for="(collection, index) in allItems.collections" :key="'search_collection_list_item_' + index"
                    class="list-group-item list-group-item-action" @click="openCollectionDetails(collection.id)">
                    {{ collection.name }}
                  </a>
                </div>
                <div v-if="wallVisible && allItems.posts.length" class="search-result-group">
                  <h4 class="mt-4 mb-2"><i class="uil-coffee"></i> {{ $t("home.Wall") }}
                    <span class="search-result-count">{{ allItems.posts_total }}</span>
                  </h4>
                  <a v-for="(post, index) in allItems.posts" :key="'search_post_list_item_' + index"
                    class="list-group-item list-group-item-action" @click="openSocialPost(post.id)">
                    {{ post.text }}
                  </a>
                </div>
                <!-- linkedin -->
                <div v-if="linkedinVisible && allItems.linkedinResults.length" class="search-result-group">
                  <h4 class="mt-4 mb-2">{{ $t('home.provider_trainings') }}
                    <span class="search-result-count">{{ allItems.linkedinResults.length }}</span>
                  </h4>
                  <a v-for="(result, index) in allItems.linkedinResults" :key="'search_linkedin_' + index"
                    class="list-group-item list-group-item-action" @click="openProviderDetails(result)">
                    {{ result.text }}
                  </a>
                </div>
                <!-- udemy -->
                <div v-if="udemyVisible && allItems.udemyResults.length" class="search-result-group">
                  <h4 class="mt-4 mb-2"> {{ $t('home.provider_udemy') }}
                    <span class="search-result-count">{{ allItems.udemyResults.length }}</span>
                  </h4>
                  <a v-for="(result, index) in allItems.udemyResults" :key="'search_udemy_' + index"
                     class="list-group-item list-group-item-action" @click="openProviderDetails(result)">
                    {{ result.text }}
                  </a>
                </div>
                <!-- harvard -->
                <div v-if="harvardVisible && allItems.harvardResults.length" class="search-result-group">
                  <h4 class="mt-4 mb-2"> {{ $t('home.provider_harvard') }}
                    <span class="search-result-count">{{ allItems.harvardResults.length }}</span>
                  </h4>
                  <a v-for="(result, index) in allItems.harvardResults" :key="'search_harvard_' + index"
                     class="list-group-item list-group-item-action" @click="openProviderDetails(result)">
                    {{ result.text }}
                  </a>
                </div>
                <!-- aeroclass -->
                <div v-if="aeroclassVisible && allItems.aeroclassResults.length" class="search-result-group">
                  <h4 class="mt-4 mb-2"> {{ $t('home.provider_aeroclass') }}
                    <span class="search-result-count">{{ allItems.aeroclassResults.length }}</span>
                  </h4>
                  <a v-for="(result, index) in allItems.aeroclassResults" :key="'search_aeroclass_' + index"
                     class="list-group-item list-group-item-action" @click="openProviderDetails(result)">
                    {{ result.text }}
                  </a>
                </div>
                <!-- coursera -->
                <div v-if="courseraVisible && allItems.courseraResults.length" class="search-result-group">
                  <h4 class="mt-4 mb-2"> {{ $t('home.provider_coursera') }}
                    <span class="search-result-count">{{ allItems.courseraResults.length }}</span>
                  </h4>
                  <a v-for="(result, index) in allItems.courseraResults" :key="'search_coursera_' + index"
                     class="list-group-item list-group-item-action" @click="openProviderDetails(result)">
                    {{ result.text }}
                  </a>
                </div>
                <div v-if="catalogVisible && allItems.catalogResults.length" class="search-result-group">
                  <h4 class="mt-4 mb-2">{{ $t("education.catalog_education") }}
                    <span class="search-result-count">{{ allItems.catalogResultsTotal }}</span>
                  </h4>
                  <a v-for="(result, index) in allItems.catalogResults" :key="'search_provider_list_item_' + index"
                    class="list-group-item list-group-item-action" @click="openCatalogDetails(result.id)">
                    {{ result.text }}
                  </a>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
  </form>
</template>

<script>
import store from "@/core/services";
import seraching_module, { GET_ITEMS, LOADING, MODULE_NAME } from "@/core/services/store/searching.module";
import DefaultLoading from "@/view/components/loading/DefaultLoading";
import axios from "axios";
import company_module, {
  MODULE_NAME as COMPANY_MODULE_NAME,
  SELF as COMPANY_SELF,
} from "@/core/services/store/company.module";

export default {
  name: "Searching",
  components: { DefaultLoading },
  data() {
    return {
      assignmentUrl: "api/assignment",
      socialPostUrl: "api/social-post",
      collectionUrl: "api/collection",
      providerSearchUrl: "api/provider-search",
      catalogSearchUrl: "api/training/user-assigned/search",
      search: "",
      page: 1,
      per_page: 5,
      order: "desc",
      sort: "id",
      assignmentOrder: "id",
      assignmentSort: "desc",
      modal: false,
      posts: null,
      assignments: null,
      collections: null,
      providerResults: null,
      catalogResults: null,
      parsedCurrentUser: null,
      udemyVisible: true,
      harvardVisible: true,
      aeroclassVisible: true,
      linkedinVisible: true,
      courseraVisible: true,
      catalogVisible: true,
      assignmentVisible: true,
      wallVisible: true,
      collectionVisible: true,
      providerAssignmentMap: {},
    };
  },
  props: {
    searchValue: "",
    loading: {
      type: Boolean,
      default: false
    },
    forceShowSearch: {
      type: Boolean,
      default: false
    },
    compactMode: {
      type: Boolean,
      default: false
    },
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(MODULE_NAME, seraching_module);
    registerStoreModule(COMPANY_MODULE_NAME, company_module);
  },
  computed: {
    company() {
      return store.getters[COMPANY_MODULE_NAME + "/" + COMPANY_SELF];
    },
    searching() {
      return this.$store.getters[MODULE_NAME + "/" + LOADING];
    },
    isSearchFieldEnabled() {
      const hostNameControl = window.location.host !== 'demirorenholdinguyum.softinya.com';
      const showSearchField = this.company?.settings?.find(x => x.key === 'show_search_field')?.value;
      const userControl = showSearchField === 1 || showSearchField === true || showSearchField === "1" || showSearchField === "true";

      return hostNameControl && userControl;
    },
    shouldShowSearchbar() {
      return this.forceShowSearch || this.isSearchFieldEnabled;
    },
    searchbarStyle() {
      const style = {
        padding: '5rem',
        borderRadius: '8px',
        backgroundSize: '115%',
        backgroundPosition: 'bottom right',
      };
      if (this.company?.search_bg_image) {
        style.background = 'url(' + this.company.search_bg_image_link + ') no-repeat right center';
        style.backgroundSize = '115%';
      }
      return style;
    },
    current_user() {
      return this.parsedCurrentUser || {};
    },
    collectionTitle() {
      const azconDomains = [
        'azcon.anzerabase.com',
        'azcon.softinya.com',
      ];
      const host = window.location.host.split(':')[0].replace(/^www\./, '').toLowerCase();

      if (azconDomains.includes(host)) {
        return this.$t("general.library");
      }

      return this.$t("sideNav.My_collections");
    },
    formattedAssignments() {
      let results = [];
      if (this.assignments && this.assignments.hasOwnProperty('data')) {
        this.assignments.data.forEach((currentItem) => {
          results.push({
            id: currentItem.id,
            name: currentItem.name,
            cover_image_link: currentItem.cover_image_link,
            description: currentItem.description ? currentItem.description : "-",
          });
        });
      }
      return results;
    },
    formattedProviderResults() {
      let results = [];
      if (this.providerResults && this.providerResults.hasOwnProperty('data')) {
        this.providerResults.data.forEach((currentItem) => {
          results.push({
            id: currentItem.id,
            text: currentItem.title,
            provider: currentItem.provider
          });
        });
      }
      return results;
    },
    formattedCatalogResults() {
      let results = [];
      if (this.catalogResults?.length > 0) {
        this.catalogResults.forEach((currentItem) => {
          results.push({
            id: currentItem.id,
            text: currentItem.name,
          });
        });
      }
      return results;
    },
    formattedPosts() {
      let results = [];
      if (this.posts && this.posts.hasOwnProperty('data')) {
        this.posts.data.forEach((currentItem) => {
          results.push({
            id: currentItem.id,
            text: currentItem.text,
          });
        });
      }
      return results;
    },
    formattedCollections() {
      let results = [];
      if (this.collections && this.collections.my_collections) {
        this.collections.my_collections.data.forEach((currentItem) => {
          results.push({
            id: currentItem.id,
            name: currentItem.name,
          });
        });
      }

      return results;
    },
    allItems() {
      const posts = this.posts;
      const assignments = this.assignments;
      const collections = this.collections ? this.collections.my_collections : null;
      const providerResults = this.formattedProviderResults;

      // Pre-compute provider groups once (instead of filtering in template on every render)
      const linkedinResults = providerResults.filter(i => i.provider === 'linkedin');
      const udemyResults = providerResults.filter(i => i.provider === 'udemy');
      const harvardResults = providerResults.filter(i => i.provider === 'harvard');
      const aeroclassResults = providerResults.filter(i => i.provider === 'aeroclass');
      const courseraResults = providerResults.filter(i => i.provider === 'coursera');

      return {
        posts: this.formattedPosts,
        assignments: this.formattedAssignments,
        collections: this.formattedCollections,
        providerResults,
        catalogResults: this.formattedCatalogResults,
        posts_total: posts ? posts.total : 0,
        assignments_total: assignments ? assignments.total : 0,
        collections_total: collections ? collections.total : 0,
        catalogResultsTotal: this.catalogResults?.length ? this.catalogResults.length : 0,
        linkedinResults,
        udemyResults,
        harvardResults,
        aeroclassResults,
        courseraResults,
      };
    },
  },
  methods: {
    async setupDashboardModules() {
      let combined = [];

      try {
        const response = await axios.get(process.env.VUE_APP_BACKEND_APP_URL + '/api/widgets', {
          params: { company_id: this.parsedCurrentUser?.company_id, by_user: true },
        });
        const data = response.data || {};
        combined = [...(data.dashboard ?? []), ...(data.menu ?? [])];
      } catch {
        return;
      }

      if (!combined.length) return;

      const isAzcon = (this.$store.getters.institutionType || 'softinya') === 'azcon';

      const getStatus = (type) => {
        const item = combined.find(el => el.type === type);
        if (item?.status !== 1) return false;
        // Azcon: kişi bazlı entegrasyon kontrolü
        if (isAzcon && item.user_custom_display === false) return false;
        return true;
      };

      this.udemyVisible = getStatus('udemy-business');
      this.linkedinVisible = getStatus('linkedin-learning');
      this.harvardVisible = getStatus('harvard-business');
      this.aeroclassVisible = getStatus('aeroclass');
      this.courseraVisible = getStatus('coursera');
      this.catalogVisible = getStatus('education-category');
      this.assignmentVisible = getStatus('my-assignment');
      this.wallVisible = getStatus('wall');
      this.collectionVisible = getStatus('my-collection');
    },
    isActiveItem(routeName, itemId) {
      return this.$route.name === routeName && String(this.$route.params.id) === String(itemId);
    },

    isActiveProviderItem(result) {
      const key = result.provider + '_' + result.id;
      const assignmentId = this.providerAssignmentMap[key];
      if (!assignmentId) return false;
      return this.isActiveItem('user.assignment-detail', assignmentId);
    },

    inputBlur: _.debounce(function () {
      this.modal = false;
    }, 150),

    closeCompactDropdown() {
      this.modal = false;

      if (this.$refs.searchInput && typeof this.$refs.searchInput.blur === "function") {
        this.$refs.searchInput.blur();
      }
    },

    getAssignmentResult() {
      let self = this;
      this.$store
          .dispatch(MODULE_NAME + "/" + GET_ITEMS, {
            url: this.assignmentUrl,
            filters: {
              search: self.search,
              page: self.page,
              per_page: self.per_page,
              sort: self.assignmentSort,
              order: self.assignmentOrder,
              by_user: true,
              with_expired: false
            },
            showLoading: true,
          })
          .then((result) => {
            if (result.status) {
              self.assignments = result.data;
            }
          });
    },
    getCollectionResult() {
      let self = this;
      this.$store
          .dispatch(MODULE_NAME + "/" + GET_ITEMS, {
            url: this.collectionUrl,
            filters: {
              search: self.search,
              page: self.page,
              per_page: self.per_page,
              sort: self.order,
              order_by: self.sort,
            },
            acceptPromise: false,
            showLoading: true,
          })
          .then((result) => {
            self.collections = result.data;
          });
    },
    getSocialPosts() {
      let self = this;
      this.$store
          .dispatch(MODULE_NAME + "/" + GET_ITEMS, {
            url: this.socialPostUrl,
            filters: {
              like: self.search,
              page: self.page,
              per_page: self.per_page,
              sort: self.sort,
              order: self.order,
            },
            showLoading: true,
          })
          .then((result) => {
            if (result.status) {
              self.posts = result.data;
            }
          });
    },
    getSearchProviderResult() {
      let self = this;
      this.$store
          .dispatch(MODULE_NAME + "/" + GET_ITEMS, {
            url: this.providerSearchUrl,
            filters: {
              q: self.search
            },
            showLoading: true,
          })
          .then((result) => {
            if (result.data.data.length) {
              self.providerResults = result.data;
            }
          });
    },
    getCatalogResult() {
      let self = this;
      this.$store
          .dispatch(MODULE_NAME + "/" + GET_ITEMS, {
            url: this.catalogSearchUrl,
            filters: {
              search: self.search
            },
            showLoading: true,
          })
          .then((result) => {
            if (result?.data?.length > 0) {
              self.catalogResults = result.data;
            }
          });
    },
    openAssignmentDetails(id) {
      this.closeCompactDropdown();
      this.$router.push({ name: "user.assignment-detail", params: { id: id } });
    },
    openSocialPost(id) {
      this.closeCompactDropdown();
      this.$router.push({ name: "user.wall", params: { id: id } });
    },
    openCollectionDetails(id) {
      this.closeCompactDropdown();
      this.$router.push({ name: "user.collection-detail", params: { id: id } });
    },
    openCatalogDetails(id) {
      this.closeCompactDropdown();
      this.$router.push({ name: "user.education-detail", params: { id: id } });
    },
    openProviderDetails(result) {
      this.closeCompactDropdown();
      axios.get(process.env.VUE_APP_BACKEND_APP_URL + "/api/provider-content-show/" + result.provider + "/" + result.id, {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + this.secureLocalStorage("token"),
        },
      })
          .then((response) => {
            if (response.data.status) {
              const key = result.provider + '_' + result.id;
              this.$set(this.providerAssignmentMap, key, response.data.assignment_id);
              this.$router.push({ name: "user.assignment-detail", params: { id: response.data.assignment_id } });
            } else {
              // TODO kontrol edilmeli 404 gelebilyior
            }
          });
    },
    getAllResults() {
      this.getCollectionResult()
      this.getSocialPosts()
      this.getAssignmentResult()
      this.getSearchProviderResult()
      this.getCatalogResult()

    },
    onSubmitSearch() {
      this.closeCompactDropdown();
      this.$emit("searchFor", this.search);
    },
  },
  mounted() {
    if (this.$route.query.search) {
      this.search = this.$route.query.search;
      this.onSubmitSearch();
    } else {
      this.getAllResults();
    }
    this.setupDashboardModules();
  },
  watch: {
    searchValue(val, prev) {
      if (val != prev) this.search = val;
    },
    search(val, prev) {
      if (val != prev) this.debouncedFilterSearch();
    },
  },
  created: function () {
    this.debouncedFilterSearch = _.debounce(this.getAllResults, 500);
    // Parse user once at creation instead of on every computed access
    try {
      this.parsedCurrentUser = JSON.parse(this.secureLocalStorage('user'));
    } catch (e) {
      this.parsedCurrentUser = {};
    }
  },
};
</script>

<style lang="scss" scoped>
// ========================================================================
// HEADER SEARCH — Compact Mode (azcon)
// ========================================================================

$hs-group-colors: (
    assign: #0f766e,
    collection: #7c3aed,
    post: #d97706,
    linkedin: #0a66c2,
    udemy: #5624d0,
    harvard: #a51c30,
    aeroclass: #0d5c63,
    coursera: #0056d2,
    catalog: #1f6feb,
);

.header-search-wrap {
  position: relative;
  width: 100%;
}

.header-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid #d5dde6;
  background: #f3f6f9;
  padding: 0 12px;
  cursor: text;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus-within {
    border-color: #b9cadf;
    box-shadow: 0 0 0 3px rgba(47, 111, 228, 0.08);
  }

  input,
  input:focus,
  input:active {
    border: 0 !important;
    outline: 0 !important;
    width: 100%;
    background: transparent !important;
    background-color: transparent !important;
    font: inherit;
    font-size: 14px;
    color: #1f2a37;
    padding: 0 !important;
    margin: 0 !important;
    min-height: auto;
    height: auto;
    line-height: normal;
    box-shadow: none !important;
  }

  input::placeholder {
    color: #8794a5;
    font-weight: 500;
    background: none !important;
  }
}

.header-search-icon {
  color: #8794a5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 16px;
  line-height: 1;
}

.header-search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #d7e0ea;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(22, 38, 58, 0.14);
  padding: 6px;
  z-index: 100;

  &-inner {
    max-height: 420px;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .hs-group-title {
    border-radius: 8px;
    border-left: 3px solid transparent;
    padding: 6px 10px;
    margin-bottom: 2px;
  }

  @each $name, $color in $hs-group-colors {
    .hs-group--#{$name} .hs-group-title {
      border-left-color: $color;
      background: rgba($color, 0.06);
      color: $color !important;

      i {
        color: $color !important;
      }
    }
  }
}

.hs-group {
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
}

.hs-group-title {
  font-size: 11px !important;
  font-weight: 700;
  color: #8794a5 !important;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  padding: 8px 10px 4px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1.4;

  i {
    font-size: 14px;
    flex-shrink: 0;
    color: var(--primaryColor, #666CFF);
  }
}

.hs-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 20px;
  background: #f0f3f7;
  color: #6b7a8d;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
  margin-left: auto;
}

.hs-item {
  width: 100%;
  border: 0;
  background: transparent;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  text-align: left;
  font: inherit;
  transition: background 0.12s ease;

  &:hover {
    background: #f2f6fb;
  }

  &:active {
    background: #e8eef6;
  }

  &--active {
    background: rgba(102, 108, 255, 0.08);

    .hs-item-label {
      color: var(--primaryColor, #666CFF);
      font-weight: 700;
    }
  }
}

.hs-item-label {
  font-size: 13px;
  color: #41556e;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

// ========================================================================
// DASHBOARD SEARCH — Normal Mode (softinya)
// ========================================================================

.z-100 {
  z-index: 100 !important;
}

.section-header-left h2 {
  font-family: var(--font-secondary);
  font-size: var(--text-2xl);
  font-weight: 600;
  text-transform: none !important;
  line-height: 1.3;
}

.top-right-slot-only {
  display: flex;
  justify-content: flex-end;
  padding: 0 !important;
  background: none !important;
  border-radius: 0 !important;
  background-image: none !important;
  background-size: unset !important;
}

.searchbar {
  h4 {
    font-family: var(--font-secondary);
    font-size: var(--text-md);
    font-weight: 600;
  }

  input {
    font-family: var(--font-primary);
  }

  a {
    font-family: var(--font-primary);
  }

  .searchbar-result {
    border-radius: 12px;
    border: 1px solid #eee;
    overflow: hidden;

    .search-result-group {
      h4 {
        font-size: 12px !important;
        font-weight: 600;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 8px 4px 6px;
        margin: 0 !important;
        border-bottom: none;
        display: flex;
        align-items: center;
        gap: 8px;
        line-height: 1.4;

        i {
          font-size: 15px;
          flex-shrink: 0;
          color: var(--primaryColor, #666CFF);
        }
      }

      a.list-group-item {
        font-size: 13px;
        padding: 8px 10px !important;
        display: flex;
        align-items: center;
        background: #fff !important;
        border: none !important;
        border-radius: 8px !important;
        margin-bottom: 4px;
        line-height: 1.4;
        color: #333;
        transition: background 0.15s ease;

        &:last-child {
          margin-bottom: 0;
        }

        &:hover {
          background: #eef0f5 !important;
          color: #333 !important;
        }

        &:focus, &:active {
          background: #e6e8ed !important;
          color: #333 !important;
        }
      }
    }
  }
}

.searchbar-top-right-slot {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
}

.searchbar-result-inner {
  max-height: 400px;
  overflow-y: auto;
  padding: 16px !important;
}

.search-result-group {
  background: #f8f9fb;
  border-radius: 10px;
  padding: 6px 12px 10px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
}

.search-result-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: 20px;
  background: #fff;
  color: #555;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  margin-left: auto;
  border: 1px solid #e8e8e8;
}

// ========================================================================
// SKELETON
// ========================================================================

$skeleton-gradient: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);

.searchbar-skeleton {
  padding: 5rem;
  border-radius: 8px;
  background-size: 115%;
  background-position: bottom right;

  .skeleton-title {
    height: 32px;
    background: $skeleton-gradient;
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 20px;
    width: 60%;
    max-width: 500px;
    border: none;
    padding: 0;
    text-transform: none !important;
  }

  .skeleton-input {
    flex: 1;
    height: auto;
    min-height: 38px;
    background: $skeleton-gradient;
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 8px;
    border: 1px solid #e5e5e5;
    padding: 10px 15px;
    font-size: 14px;
  }

  .skeleton-button {
    width: auto;
    height: auto;
    min-width: auto;
    background: $skeleton-gradient;
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border: none;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: transparent;

    i {
      opacity: 0;
    }
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// ========================================================================
// RESPONSIVE — Mobile
// ========================================================================

@media (max-width: 640px) {
  .searchbar {
    padding: 1.5rem !important;
    background-size: cover !important;
    background-position: center !important;

    h2 {
      font-size: var(--text-lg) !important;
      line-height: 1.4;
      margin-bottom: 0.75rem;
    }

    .input-items-group {
      flex-direction: row;
      gap: 8px;
      align-items: center;

      input {
        font-size: 16px;
        padding: 12px;
        min-height: 48px;
        margin-top: 0 !important;
        flex: 1;
        border-radius: 10px;
        touch-action: manipulation;
      }

      .btn {
        min-width: 48px;
        min-height: 48px;
        padding: 0;
        margin: 0;
        margin-top: 0 !important;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        align-self: center;

        &:active {
          transform: scale(0.98);
          opacity: 0.85;
        }

        i {
          margin: 0;
          font-size: 18px;
          line-height: 1;
        }
      }
    }

    .searchbar-result {
      max-height: 70vh !important;
      left: -8px;
      right: -8px;
      border-radius: 14px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      margin-top: 8px !important;
      overflow: hidden;
      border: 1px solid #f0f0f0;

      > .list-group-item {
        max-height: 65vh !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        padding: 12px !important;
        border: none !important;
        -webkit-overflow-scrolling: touch;
        background: #fff;
      }

      .search-result-group {
        background: #f8f9fb;
        border-radius: 14px;
        padding: 4px 8px 8px;
        margin-bottom: 8px;

        &:last-child {
          margin-bottom: 0;
        }
      }

      h4 {
        font-size: 12px;
        font-weight: 600;
        color: #888;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        padding: 8px 4px 6px;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        border-bottom: none;
        display: flex;
        align-items: center;
        gap: 8px;
        line-height: 1.4;

        i {
          font-size: 15px;
          flex-shrink: 0;
          color: var(--primaryColor, #666CFF);
        }
      }

      .search-result-count {
        min-width: 20px;
        height: 20px;
        padding: 0 6px;
        color: #666;
      }

      a.list-group-item {
        font-size: 14px;
        padding: 12px 8px !important;
        min-height: 48px;
        display: flex;
        align-items: center;
        background: #fff !important;
        border: none !important;
        border-radius: 10px !important;
        margin-bottom: 4px;
        line-height: 1.4;
        color: #333;
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        transition: none;

        &:last-child {
          margin-bottom: 0;
        }

        &:active {
          background: #eef0f5 !important;
          transform: scale(0.98);
        }
      }

      a:hover {
        background-color: inherit !important;
        color: inherit !important;
        opacity: inherit !important;
        transform: none !important;
        box-shadow: inherit !important;
        border-color: inherit !important;
      }
    }
  }

  .searchbar-top-right-slot {
    .btn:hover, button:hover, a:hover {
      background-color: inherit !important;
      color: inherit !important;
      opacity: inherit !important;
      transform: none !important;
      box-shadow: inherit !important;
      border-color: inherit !important;
    }
  }

  .searchbar-skeleton {
    padding: 1.5rem !important;
    background-size: cover !important;
    background-position: center !important;

    .skeleton-title {
      height: 24px;
      width: 80%;
      margin-bottom: 0.75rem;
      line-height: 1.4;
    }

    .skeleton-input {
      min-height: 48px;
      padding: 12px;
      margin-top: 0 !important;
      border-radius: 10px;
    }

    .skeleton-button {
      min-width: 48px;
      min-height: 48px;
      padding: 0;
      margin: 0;
      border-radius: 10px;
    }
  }
}

// ========================================================================
// RESPONSIVE — Tablet
// ========================================================================

@media (min-width: 641px) and (max-width: 959px) {
  .searchbar {
    padding: 2.5rem !important;

    h2 {
      font-size: var(--text-xl) !important;
    }
  }

  .searchbar-skeleton {
    padding: 2.5rem !important;

    .skeleton-title {
      height: 28px;
    }
  }
}
</style>
